import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from 'Utils/api';
import styled from 'styled-components';

const FormWrapper = styled.form`
  margin-top: 3rem;
  padding: 2rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: 'Abel', sans-serif;
  background: #fafafa;
`;

const Heading = styled.h2`
  font-family: 'Abel', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  margin: 0 0 0.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Intro = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
`;

const Row = styled.div<{ cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols || 1}, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const baseField = `
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  padding: 0.65rem 0.85rem;
  border: 1.5px solid #d6d6d6;
  border-radius: 6px;
  background: white;

  &:focus {
    outline: none;
    border-color: #b8964a;
  }
`;

const Input = styled.input` ${baseField} `;
const TextArea = styled.textarea` ${baseField} resize: vertical; min-height: 100px; `;

const WatchSnapshot = styled.div`
  background: rgba(184, 150, 74, 0.08);
  border-left: 3px solid #b8964a;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
`;

const SubmitButton = styled.button`
  background: #b8964a;
  color: white;
  border: none;
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.9rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) { background: #a0864a; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Notice = styled.div<{ $kind: 'error' | 'success' }>`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background: ${({ $kind }) => $kind === 'success' ? 'rgba(46, 125, 50, 0.12)' : 'rgba(198, 40, 40, 0.12)'};
  color: ${({ $kind }) => $kind === 'success' ? '#2e7d32' : '#c62828'};
  border-left: 3px solid ${({ $kind }) => $kind === 'success' ? '#2e7d32' : '#c62828'};
`;

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  orderNumber: string;
  orderDate: string;
  reason: string;
  code: string;
}

const EMPTY: FormState = {
  firstName: '', lastName: '', email: '', phone: '',
  street: '', postalCode: '', city: '', country: '',
  orderNumber: '', orderDate: '', reason: '', code: '',
};

// Read a query param case-insensitively
const getParam = (sp: URLSearchParams, ...keys: string[]): string => {
  for (const k of keys) {
    const v = sp.get(k);
    if (v !== null && v !== '') return v;
  }
  return '';
};

const WithdrawalForm: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [watchInfo, setWatchInfo] = useState<{ brand: string; model: string; referenceNumber: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill from URL params (case-insensitive aliases supported)
  useEffect(() => {
    const next: FormState = {
      firstName:   getParam(searchParams, 'firstName', 'firstname', 'vorname'),
      lastName:    getParam(searchParams, 'lastName', 'lastname', 'nachname'),
      email:       getParam(searchParams, 'email', 'mail'),
      phone:       getParam(searchParams, 'phone', 'tel', 'telefon'),
      street:      getParam(searchParams, 'street', 'strasse', 'address'),
      postalCode:  getParam(searchParams, 'postalCode', 'postalcode', 'plz', 'zip'),
      city:        getParam(searchParams, 'city', 'stadt', 'ort'),
      country:     getParam(searchParams, 'country', 'countryCode', 'land').toUpperCase(),
      orderNumber: getParam(searchParams, 'orderNumber', 'order', 'bestellnummer'),
      orderDate:   getParam(searchParams, 'orderDate', 'orderdate', 'bestelldatum'),
      reason:      getParam(searchParams, 'reason', 'grund'),
      code:        getParam(searchParams, 'code', 'shortcode', 'short_code').toUpperCase(),
    };
    setForm(next);
  }, [searchParams]);

  // Resolve watch by short code
  useEffect(() => {
    if (!form.code) { setWatchInfo(null); return; }
    (async () => {
      try {
        const res = await apiClient.get(`/api/watches/by-short-code/${encodeURIComponent(form.code)}`);
        if (res.data?.status === 'success' && res.data.watch) {
          setWatchInfo({
            brand: res.data.watch.brand,
            model: res.data.watch.model,
            referenceNumber: res.data.watch.referenceNumber,
          });
        } else {
          setWatchInfo(null);
        }
      } catch {
        setWatchInfo(null);
      }
    })();
  }, [form.code]);

  const update = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [k]: e.target.value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError(t('withdrawal.error_required'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError(t('withdrawal.error_email'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone || undefined,
        street: form.street || undefined,
        postalCode: form.postalCode || undefined,
        city: form.city || undefined,
        countryCode: form.country || undefined,
        orderNumber: form.orderNumber || undefined,
        orderDate: form.orderDate || undefined,
        watchShortCode: form.code || undefined,
        reason: form.reason || undefined,
      };
      const res = await apiClient.post(`/api/withdrawal`, payload);
      if (res.data?.status === 'success') {
        setSuccess(true);
        setForm(EMPTY);
        setWatchInfo(null);
      } else {
        setError(res.data?.message || t('withdrawal.error_generic'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('withdrawal.error_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormWrapper onSubmit={onSubmit} noValidate data-testid="withdrawal-form">
      <Heading>{t('withdrawal.form_title')}</Heading>
      <Intro>{t('withdrawal.form_intro')}</Intro>

      {watchInfo && (
        <WatchSnapshot data-testid="withdrawal-watch-info">
          <strong>{t('withdrawal.watch_label')}:</strong>{' '}
          {watchInfo.brand} {watchInfo.model}
          {watchInfo.referenceNumber ? ` · Ref. ${watchInfo.referenceNumber}` : ''}
          {form.code ? ` · Code ${form.code}` : ''}
        </WatchSnapshot>
      )}

      <Row cols={2}>
        <FieldGroup>
          <Label>{t('withdrawal.first_name')} *</Label>
          <Input value={form.firstName} onChange={update('firstName')} data-testid="wd-firstName" />
        </FieldGroup>
        <FieldGroup>
          <Label>{t('withdrawal.last_name')} *</Label>
          <Input value={form.lastName} onChange={update('lastName')} data-testid="wd-lastName" />
        </FieldGroup>
      </Row>

      <Row cols={2}>
        <FieldGroup>
          <Label>{t('withdrawal.email')} *</Label>
          <Input type="email" value={form.email} onChange={update('email')} data-testid="wd-email" />
        </FieldGroup>
        <FieldGroup>
          <Label>{t('withdrawal.phone')}</Label>
          <Input value={form.phone} onChange={update('phone')} data-testid="wd-phone" />
        </FieldGroup>
      </Row>

      <Row cols={1}>
        <FieldGroup>
          <Label>{t('withdrawal.street')}</Label>
          <Input value={form.street} onChange={update('street')} data-testid="wd-street" />
        </FieldGroup>
      </Row>

      <Row cols={3}>
        <FieldGroup>
          <Label>{t('withdrawal.postal_code')}</Label>
          <Input value={form.postalCode} onChange={update('postalCode')} data-testid="wd-postalCode" />
        </FieldGroup>
        <FieldGroup>
          <Label>{t('withdrawal.city')}</Label>
          <Input value={form.city} onChange={update('city')} data-testid="wd-city" />
        </FieldGroup>
        <FieldGroup>
          <Label>{t('withdrawal.country')}</Label>
          <Input value={form.country} onChange={update('country')} maxLength={3} data-testid="wd-country" />
        </FieldGroup>
      </Row>

      <Row cols={2}>
        <FieldGroup>
          <Label>{t('withdrawal.order_number')}</Label>
          <Input value={form.orderNumber} onChange={update('orderNumber')} data-testid="wd-orderNumber" />
        </FieldGroup>
        <FieldGroup>
          <Label>{t('withdrawal.order_date')}</Label>
          <Input value={form.orderDate} onChange={update('orderDate')} data-testid="wd-orderDate" />
        </FieldGroup>
      </Row>

      <Row cols={1}>
        <FieldGroup>
          <Label>{t('withdrawal.reason')}</Label>
          <TextArea
            value={form.reason}
            onChange={update('reason')}
            rows={4}
            maxLength={1500}
            data-testid="wd-reason"
          />
        </FieldGroup>
      </Row>

      <SubmitButton type="submit" disabled={submitting} data-testid="wd-submit">
        {submitting ? t('withdrawal.submitting') : t('withdrawal.submit')}
      </SubmitButton>

      {error && <Notice $kind="error" data-testid="wd-error">{error}</Notice>}
      {success && <Notice $kind="success" data-testid="wd-success">{t('withdrawal.success')}</Notice>}
    </FormWrapper>
  );
};

export default WithdrawalForm;
